'use client';

import { useEffect, useCallback, useMemo } from 'react';
import {
    type PageContext,
    type CtaContext,
    trackCtaClick,
    trackPageView,
    getStoredCtaSource,
    clearStoredCtaSource,
    captureLandingPage,
    buildTrackedCTAUrl,
} from '@/lib/cta-tracking';

/**
 * Hook for page-level CTA tracking.
 *
 * Usage:
 *   const { handleCtaClick, buildCtaHref } = useCtaTracking({
 *     page_type: 'occasion',
 *     page_name: 'Birthday',
 *     page_slug: 'birthday-video',
 *     page_category: 'birthday',
 *   });
 */
export function useCtaTracking(pageContext: PageContext) {
    // Track page view and capture landing page context on mount
    useEffect(() => {
        captureLandingPage(pageContext);
        trackPageView(pageContext);
    }, [pageContext]);

    const handleCtaClick = useCallback(
        (ctaContext: CtaContext) => {
            trackCtaClick(pageContext, ctaContext);
        },
        [pageContext],
    );

    const buildCtaHref = useCallback(
        (baseUrl: string, ctaContext: CtaContext): string => {
            return buildTrackedCTAUrl(baseUrl, pageContext, ctaContext);
        },
        [pageContext],
    );

    return useMemo(
        () => ({
            handleCtaClick,
            buildCtaHref,
            getStoredCtaSource,
            clearStoredCtaSource,
        }),
        [handleCtaClick, buildCtaHref],
    );
}
